/**
 * Small, dependency-free charts for the guest-app usage statistics
 * (client-admin states/statistics, saas-admin states/analytics). One measure
 * per chart, one hue (brand green) — every chart here shows a magnitude, none
 * an identity, so there is no palette to pick and nothing to tell apart by
 * colour.
 *
 * - lineChart  — visitors over time: the plot is an SVG stretched to the box
 *   (non-scaling 2px stroke, 10 % area wash), while axis labels, crosshair,
 *   marker and tooltip are HTML positioned in percent, so text never scales
 *   with the plot. Pointer and arrow keys snap the crosshair to a day.
 * - columns    — weekday / hour: HTML columns, ≤ 24px, 4px rounded tops,
 *   a per-column tooltip on hover and focus.
 * - bars       — sections / languages / devices: label · bar · value, every
 *   value written out, so nothing hides behind a hover.
 *
 * Builders return HTML strings built from numbers and pre-escaped labels;
 * bind() wires the interaction afterwards and puts tooltip text in with
 * textContent. `listen` is the state's tracked addEventListener, so the
 * listeners go away with the state.
 *
 * Styles: @belturo/design-system components/usage-charts.css. The client-admin
 * has the original of this module (core/UsageCharts.js) — keep the two in step.
 */

export function esc( value )
{
    return String( value ).replace( /[&<>"']/g, ( c ) => (
        { "&" : "&amp;", "<" : "&lt;", ">" : "&gt;", '"' : "&quot;", "'" : "&#39;" }[ c ] ) );
}

/** A round axis maximum and its step: 0 · step · … ≥ max, about four steps. */
export function niceScale( max, steps = 4 )
{
    if ( !( max > 0 ) )
    {
        return { max : steps, step : 1 };
    }
    const raw = max / steps,
          mag = Math.pow( 10, Math.floor( Math.log10( raw ) ) ),
          norm = raw / mag,
          nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
    let step = nice * mag;
    if ( step < 1 )
    {
        step = 1;
    }
    return { max : Math.ceil( max / step ) * step, step };
}

// ~10 % headroom, so the peak and its label never touch the top edge.
function lineScale( points )
{
    return niceScale( Math.max( 0, ...points.map( ( p ) => p.value ) ) * 1.1 );
}

/**
 * @param {Array<{label, title, value, rows: Array<{label, value}>}>} points
 *        label = x-axis text (may be ""), title = tooltip heading,
 *        value = the plotted number, rows = tooltip lines under it
 * @param {Object} opts { format(n), label (aria), partialLast: the last point is
 *        a period still running (today, this week) — drawn dashed, so a
 *        half-counted day does not read as a slump }
 */
export function lineChart( points, opts )
{
    const n = points.length,
          scale = lineScale( points ),
          x = ( i ) => n > 1 ? i / ( n - 1 ) * 1000 : 500,
          y = ( v ) => 200 - v / scale.max * 200;

    const pt = ( i ) => x( i ).toFixed( 1 ) + " " + y( points[ i ].value ).toFixed( 1 ),
          all = points.map( ( p, i ) => ( i ? "L" : "M" ) + pt( i ) ).join( "" ),
          partial = opts.partialLast && n > 1,
          solid = partial ? points.slice( 0, n - 1 ).map( ( p, i ) => ( i ? "L" : "M" ) + pt( i ) ).join( "" ) : all,
          area = all + "L" + x( n - 1 ).toFixed( 1 ) + " 200L" + x( 0 ).toFixed( 1 ) + " 200Z";

    const ticks = [];
    for ( let v = 0; v <= scale.max; v += scale.step )
    {
        ticks.push( v );
    }

    // ~6 dates along the bottom, always the first and the last; every other
    // one in between is "minor" and dropped on narrow screens (CSS).
    const every = Math.max( 1, Math.ceil( n / 6 ) ),
          xLabels = points
              .map( ( p, i ) => ( { p, i } ) )
              .filter( ( { i } ) => i % every === 0 || i === n - 1 )
              .filter( ( { i } ) => i === n - 1 || ( n - 1 - i ) >= every * 0.8 );

    // The one direct label: the peak.
    let peak = 0;
    points.forEach( ( p, i ) => { if ( p.value > points[ peak ].value ) peak = i; } );

    return '<div class="uc-line" data-uc="line">' +
        '<div class="uc-line__y">' +
            ticks.map( ( v ) => '<span style="bottom:' + ( v / scale.max * 100 ) + '%">' + esc( opts.format( v ) ) + '</span>' ).join( "" ) +
        '</div>' +
        '<div class="uc-line__plot" tabindex="0" role="img" aria-label="' + esc( opts.label ) + '">' +
            '<svg viewBox="0 0 1000 200" preserveAspectRatio="none" aria-hidden="true">' +
                ticks.map( ( v ) => '<line class="uc-line__grid" x1="0" x2="1000" y1="' + y( v ) + '" y2="' + y( v ) + '"/>' ).join( "" ) +
                '<path class="uc-line__area" d="' + area + '"/>' +
                '<path class="uc-line__stroke" d="' + solid + '"/>' +
                ( partial ? '<path class="uc-line__stroke uc-line__stroke--partial" d="M' + pt( n - 2 ) + 'L' + pt( n - 1 ) + '"/>' : '' ) +
            '</svg>' +
            ( points[ peak ] && points[ peak ].value > 0
                ? '<span class="uc-line__peak" style="left:' + ( x( peak ) / 10 ) + '%;bottom:' + ( points[ peak ].value / scale.max * 100 ) + '%">' + esc( opts.format( points[ peak ].value ) ) + '</span>'
                : '' ) +
            '<span class="uc-line__cross" hidden></span>' +
            '<span class="uc-line__dot" hidden></span>' +
            '<div class="uc-tip" role="status" hidden></div>' +
        '</div>' +
        '<div class="uc-line__x">' +
            xLabels.map( ( { p, i }, k ) =>
                '<span style="left:' + ( x( i ) / 10 ) + '%"' +
                    ( i === 0 ? ' class="uc-line__x-first"' : i === n - 1 ? ' class="uc-line__x-last"' : ( k % 2 ? ' class="uc-line__x-minor"' : '' ) ) +
                '>' + esc( p.label ) + '</span>' ).join( "" ) +
        '</div>' +
    '</div>';
}

/**
 * @param {Array<{label, title, value, display, rows?}>} items
 *        label = under the column ("" to skip), display = formatted value
 * @param {Object} opts { label (aria), showValues: bool — value on each cap }
 */
export function columns( items, opts )
{
    const max = Math.max( 0, ...items.map( ( i ) => i.value ) );
    return '<div class="uc-cols' + ( items.length > 12 ? ' uc-cols--dense' : '' ) + ( opts.showValues ? ' uc-cols--values' : '' ) + '" data-uc="cols" role="list" aria-label="' + esc( opts.label ) + '">' +
        items.map( ( item, i ) =>
            '<div class="uc-cols__col" role="listitem" tabindex="0" data-i="' + i + '" aria-label="' + esc( item.title + ": " + item.display ) + '">' +
                '<span class="uc-cols__track">' +
                    '<span class="uc-cols__bar" style="height:' + ( max > 0 ? item.value / max * 100 : 0 ) + '%">' +
                        ( opts.showValues ? '<span class="uc-cols__value">' + esc( item.display ) + '</span>' : '' ) +
                    '</span>' +
                '</span>' +
                '<span class="uc-cols__label">' + esc( item.label ) + '</span>' +
            '</div>'
        ).join( "" ) +
        '<div class="uc-tip" role="status" hidden></div>' +
    '</div>';
}

/**
 * @param {Array<{label, value, display, share?}>} items  sorted as they should show
 */
export function bars( items )
{
    const max = Math.max( 0, ...items.map( ( i ) => i.value ) );
    return '<ul class="uc-bars">' +
        items.map( ( item ) =>
            '<li class="uc-bars__row">' +
                '<span class="uc-bars__label">' + esc( item.label ) + '</span>' +
                '<span class="uc-bars__track"><span class="uc-bars__fill" style="width:' + ( max > 0 ? item.value / max * 100 : 0 ) + '%"></span></span>' +
                '<span class="uc-bars__value">' + esc( item.display ) + '</span>' +
            '</li>'
        ).join( "" ) +
    '</ul>';
}

/**
 * Wires hover / focus / arrow keys for every chart inside host.
 *
 * @param {HTMLElement} host
 * @param {Object} data { line: points[], cols: { [index of uc-cols in host]: items[] } }
 * @param {Function} listen ( el, type, handler ) — the state's tracked addEventListener
 */
export function bind( host, data, listen )
{
    const line = host.querySelector( '[data-uc="line"]' );
    if ( line && data.line && data.line.length )
    {
        bindLine( line, data.line, listen );
    }

    host.querySelectorAll( '[data-uc="cols"]' ).forEach( ( el, idx ) =>
    {
        if ( data.cols && data.cols[ idx ] )
        {
            bindColumns( el, data.cols[ idx ], listen );
        }
    } );
}

function fillTip( tip, title, rows )
{
    tip.textContent = "";
    const head = document.createElement( "div" );
    head.className = "uc-tip__title";
    head.textContent = title;
    tip.appendChild( head );
    rows.forEach( ( r, i ) =>
    {
        const row = document.createElement( "div" );
        row.className = "uc-tip__row" + ( i === 0 ? " uc-tip__row--main" : "" );
        const value = document.createElement( "strong" );
        value.textContent = r.value;
        const label = document.createElement( "span" );
        label.textContent = r.label;
        row.append( value, label );
        tip.appendChild( row );
    } );
    tip.hidden = false;
}

function bindLine( root, points, listen )
{
    const plot = root.querySelector( ".uc-line__plot" ),
          cross = root.querySelector( ".uc-line__cross" ),
          dot = root.querySelector( ".uc-line__dot" ),
          tip = root.querySelector( ".uc-tip" ),
          n = points.length,
          max = lineScale( points ).max;
    let current = -1;

    const show = ( i ) =>
    {
        current = Math.max( 0, Math.min( n - 1, i ) );
        const p = points[ current ],
              left = n > 1 ? current / ( n - 1 ) * 100 : 50,
              bottom = p.value / max * 100;
        cross.style.left = left + "%";
        dot.style.left = left + "%";
        dot.style.bottom = bottom + "%";
        cross.hidden = dot.hidden = false;
        fillTip( tip, p.title, p.rows );
        // Keep the tooltip inside the plot: flip to the left of the crosshair past the middle.
        tip.style.left = left + "%";
        tip.classList.toggle( "uc-tip--left", left > 55 );
    };
    const hide = () =>
    {
        cross.hidden = dot.hidden = tip.hidden = true;
    };

    listen( plot, "pointermove", ( e ) =>
    {
        const box = plot.getBoundingClientRect();
        show( Math.round( ( e.clientX - box.left ) / box.width * ( n - 1 ) ) );
    } );
    listen( plot, "pointerleave", () => { if ( document.activeElement !== plot ) hide(); } );
    listen( plot, "focus", () => show( current < 0 ? n - 1 : current ) );
    listen( plot, "blur", hide );
    listen( plot, "keydown", ( e ) =>
    {
        const step = { ArrowLeft : -1, ArrowRight : 1, Home : -n, End : n }[ e.key ];
        if ( step )
        {
            e.preventDefault();
            show( ( current < 0 ? n - 1 : current ) + step );
        }
    } );
}

function bindColumns( root, items, listen )
{
    const tip = root.querySelector( ".uc-tip" );
    const show = ( col ) =>
    {
        const item = items[ +col.getAttribute( "data-i" ) ];
        if ( !item )
        {
            return;
        }
        fillTip( tip, item.title, item.rows || [ { label : "", value : item.display } ] );
        const rootBox = root.getBoundingClientRect(),
              box = col.getBoundingClientRect(),
              left = ( box.left + box.width / 2 - rootBox.left ) / rootBox.width * 100;
        tip.style.left = left + "%";
        tip.classList.toggle( "uc-tip--left", left > 60 );
        root.querySelectorAll( ".uc-cols__col--hover" ).forEach( ( c ) => c.classList.remove( "uc-cols__col--hover" ) );
        col.classList.add( "uc-cols__col--hover" );
    };
    const hide = () =>
    {
        tip.hidden = true;
        root.querySelectorAll( ".uc-cols__col--hover" ).forEach( ( c ) => c.classList.remove( "uc-cols__col--hover" ) );
    };

    root.querySelectorAll( ".uc-cols__col" ).forEach( ( col ) =>
    {
        listen( col, "pointerenter", () => show( col ) );
        listen( col, "focus", () => show( col ) );
        listen( col, "blur", hide );
    } );
    listen( root, "pointerleave", hide );
}
